import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle,
  Loader2,
  MapPin,
  Phone,
  CreditCard,
  AlertCircle,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { format, addDays, isBefore, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

function redirectToCheckout(url) {
  const nextUrl = String(url || '').trim();
  if (!nextUrl) return;
  // Stripe checkout must run in a top-level browsing context.
  if (window.top && window.top !== window.self) {
    try {
      window.top.location.href = nextUrl;
      return;
    } catch (_) {
      const openedTop = window.open(nextUrl, '_top', 'noopener,noreferrer');
      if (openedTop) return;
      window.open(nextUrl, '_blank', 'noopener,noreferrer');
      return;
    }
  }
  window.location.assign(nextUrl);
}

// Calendar link generators
const generateCalendarLinks = (booking, restaurant, selectedDate) => {
  const selectedDateString = selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
    ? format(selectedDate, 'yyyy-MM-dd')
    : '';
  const bookingDate = booking.booking_date || booking.date || selectedDateString;
  const bookingTime = booking.booking_time || booking.time;

  if (!bookingDate || !bookingTime) {
    return null;
  }

  const startDate = new Date(`${bookingDate}T${bookingTime}:00`);
  if (Number.isNaN(startDate.getTime())) {
    return null;
  }
  const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 hour duration
  
  const title = encodeURIComponent(`Reservation at ${restaurant.name}`);
  const manageUrl = booking.manage_url || (
    booking.cancellation_token && booking.id
      ? `${window.location.origin}/cancel/${booking.id}/${booking.cancellation_token}`
      : null
  );
  const descriptionLines = [
    `Restaurant: ${restaurant.name}`,
    `Guests: ${booking.party_size}`,
    `Confirmation: #${booking.id.slice(0, 8).toUpperCase()}`,
    `Phone: ${restaurant.phone}`,
  ];
  if (manageUrl) {
    descriptionLines.push(`Manage or cancel booking: ${manageUrl}`);
  }
  const description = encodeURIComponent(descriptionLines.join('\n'));
  const location = encodeURIComponent(restaurant.address);
  
  // Format dates for different calendar types
  const formatForGoogle = (date) => date.toISOString().replace(/-|:|\.\d{3}/g, '').slice(0, -1);
  const formatForICS = (date) => date.toISOString().replace(/-|:|\.\d{3}/g, '').slice(0, -1) + 'Z';
  
  const googleStart = formatForGoogle(startDate);
  const googleEnd = formatForGoogle(endDate);
  
  // Google Calendar
  const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${googleStart}/${googleEnd}&details=${description}&location=${location}`;
  
  // Outlook Web
  const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}&body=${description}&location=${location}`;
  
  // Generate ICS file content for Apple Calendar / Download
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//DineSpot//Booking//EN
BEGIN:VEVENT
UID:${booking.id}@dinespot
DTSTAMP:${formatForICS(new Date())}
DTSTART:${formatForICS(startDate)}
DTEND:${formatForICS(endDate)}
SUMMARY:Reservation at ${restaurant.name}
DESCRIPTION:${descriptionLines.join('\\n')}
LOCATION:${restaurant.address}
END:VEVENT
END:VCALENDAR`;
  
  const icsBlob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const icsUrl = URL.createObjectURL(icsBlob);
  
  return {
    google: googleUrl,
    outlook: outlookUrl,
    ics: icsUrl,
    icsFilename: `reservation-${restaurant.name.replace(/\s+/g, '-').toLowerCase()}-${booking.id.slice(0, 8)}.ics`
  };
};

const STEPS = [
  { id: 'date', title: 'Select Date', icon: CalendarIcon },
  { id: 'time', title: 'Choose Time', icon: Clock },
  { id: 'details', title: 'Your Details', icon: Users },
  { id: 'confirm', title: 'Confirmed', icon: CheckCircle }
];

export default function BookingWidget() {
  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  
  // Booking data
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [partySize, setPartySize] = useState('2');
  const [formData, setFormData] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('ds_guest_info') || '{}');
      return {
        name: saved.name || '',
        email: saved.email || '',
        phone: saved.phone || '',
        specialRequests: '',
        allergies: '',
      };
    } catch {
      return { name: '', email: '', phone: '', specialRequests: '', allergies: '' };
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  
  // Deposit state
  const [depositInfo, setDepositInfo] = useState(null);
  const [checkingDeposit, setCheckingDeposit] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);
  const [pendingBookingId, setPendingBookingId] = useState(null); // Track if we're completing a pending booking

  // Check for payment return or pending booking
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const bookingId = searchParams.get('booking_id');
    const pendingId = searchParams.get('pending');
    
    if (sessionId && bookingId) {
      checkPaymentStatus(sessionId, bookingId);
    } else if (pendingId) {
      loadPendingBooking(pendingId);
    }
  }, [searchParams]);

  // Load a pending booking that needs deposit payment
  const loadPendingBooking = async (pendingId) => {
    try {
      const response = await axios.get(`${API}/bookings/pending/${pendingId}`);
      const pending = response.data;
      
      if (pending.status !== 'pending_deposit') {
        toast.error('This booking has already been processed');
        return;
      }
      
      // Store the pending booking ID
      setPendingBookingId(pendingId);
      
      // Pre-fill the form with pending booking data
      setSelectedDate(new Date(pending.booking_date));
      setSelectedTime(pending.booking_time);
      setPartySize(String(pending.party_size)); // Set the partySize state too!
      setFormData({
        name: pending.customer_name,
        email: pending.customer_email,
        phone: pending.customer_phone || '',
        partySize: String(pending.party_size),
        specialRequests: '',
        allergies: pending.allergies || ''
      });
      
      // Set deposit info - this is required!
      setDepositInfo({
        deposit_required: true,
        amount: pending.deposit_amount,
        currency: pending.deposit_currency || 'gbp',
        is_refundable: true,
        refund_hours_before: 24
      });
      
      // Go directly to step 2 (details/payment step)
      setStep(2);
      setTermsAccepted(false);
      setShowFullTerms(true);
      
      toast.info('Please review your booking and complete the deposit payment');
    } catch (error) {
      console.error('Failed to load pending booking:', error);
      toast.error('Booking not found or already processed');
    }
  };

  const checkPaymentStatus = async (sessionId, bookingId) => {
    setPaymentProcessing(true);
    setStep(3); // Show confirmation step
    
    const pollStatus = async (attempts = 0) => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
          
          // Try to fetch the confirmed booking (it's no longer pending_deposit)
          try {
            // Use the admin endpoint or create a new public endpoint for confirmed bookings
            const bookingResponse = await axios.get(`${API}/booking/${bookingId}/details`);
            setConfirmation({
              id: bookingResponse.data.id,
              booking_date: bookingResponse.data.booking_date,
              booking_time: bookingResponse.data.booking_time,
              party_size: bookingResponse.data.party_size,
              deposit_amount: bookingResponse.data.deposit_amount,
              cancellation_token: bookingResponse.data.cancellation_token,
              manage_url: bookingResponse.data.manage_url,
            });
          } catch (err) {
            console.error('Failed to fetch booking details, using fallback:', err);
            // Fallback: set confirmation from URL params or session data
            setConfirmation({
              id: bookingId,
              booking_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
              booking_time: selectedTime || '',
              party_size: parseInt(partySize) || 2,
              manage_url: response.data.manage_url,
            });
          }
          
          toast.success('Payment successful! Your booking is confirmed.');
          setPaymentProcessing(false);
          return;
        } else if (response.data.status === 'expired') {
          setPaymentStatus('failed');
          toast.error('Payment was not completed. Please try again.');
          setPaymentProcessing(false);
          return;
        }
        
        // Keep polling if not resolved
        if (attempts < 30) {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        } else {
          setPaymentStatus('pending_confirmation');
          setPaymentProcessing(false);
          toast.info('Stripe is still confirming the payment. Please check again in a moment.');
        }
      } catch (error) {
        console.error('Payment status check failed:', error);
        try {
          const bookingResponse = await axios.get(`${API}/booking/${bookingId}/details`);
          if (bookingResponse.data.deposit_status === 'paid') {
            setPaymentStatus('success');
            setConfirmation({
              id: bookingResponse.data.id,
              booking_date: bookingResponse.data.booking_date,
              booking_time: bookingResponse.data.booking_time,
              party_size: bookingResponse.data.party_size,
              deposit_amount: bookingResponse.data.deposit_amount,
            });
            setPaymentProcessing(false);
            toast.success('Payment successful! Your booking is confirmed.');
            return;
          }
        } catch (fallbackError) {
          console.error('Payment fallback booking check failed:', fallbackError);
        }
        setPaymentStatus('error');
        setPaymentProcessing(false);
      }
    };
    
    pollStatus();
  };

  useEffect(() => {
    fetchRestaurant();
  }, [restaurantId]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability();
    }
  }, [selectedDate]);

  // Check deposit when date or party size changes
  useEffect(() => {
    if (selectedDate && partySize) {
      checkDepositRequirements();
    }
  }, [selectedDate, partySize]);

  const fetchRestaurant = async () => {
    try {
      const response = await axios.get(`${API}/restaurant/${restaurantId}/info`);
      setRestaurant(response.data);
    } catch (error) {
      toast.error('Restaurant not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    setLoadingSlots(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/availability/${restaurantId}?date=${dateStr}`);
      setSlots(response.data.slots.filter(s => s.available_tables > 0));
    } catch (error) {
      toast.error('Failed to load availability');
    } finally {
      setLoadingSlots(false);
    }
  };

  const checkDepositRequirements = async () => {
    if (!selectedDate) return;
    
    setCheckingDeposit(true);
    try {
      const response = await axios.post(`${API}/deposit/check`, {
        restaurant_id: restaurantId,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        party_size: parseInt(partySize)
      });
      setDepositInfo(response.data);
    } catch (error) {
      console.error('Deposit check failed:', error);
      setDepositInfo(null);
    } finally {
      setCheckingDeposit(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // If this is a pending booking (from phone booking deposit link), just initiate payment
      if (pendingBookingId) {
        await initiatePayment(pendingBookingId);
        return;
      }
      
      // Otherwise, create a new booking
      const response = await axios.post(`${API}/bookings`, {
        restaurant_id: restaurantId,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        party_size: parseInt(partySize),
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: selectedTime,
        special_requests: formData.specialRequests || null,
        allergies: formData.allergies || null
      });
      
      const booking = response.data.booking;
      setConfirmation(booking);

      // Save guest details for next visit
      try {
        localStorage.setItem('ds_guest_info', JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        }));
      } catch { /* ignore storage errors */ }
      
      // Check if deposit is required
      if (booking.deposit_required && booking.deposit_amount > 0) {
        // Create payment session and redirect to Stripe
        await initiatePayment(booking.id);
      } else {
        setStep(3);
        toast.success('Booking confirmed!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed. Please try again.');
      setSubmitting(false);
    }
  };

  const initiatePayment = async (bookingId) => {
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/create-session?booking_id=${bookingId}&origin_url=${encodeURIComponent(originUrl)}`);
      
      redirectToCheckout(response.data.checkout_url);
    } catch (error) {
      toast.error('Failed to initialize payment. Please try again.');
      setSubmitting(false);
    }
  };

  const canSelectDate = (date) => {
    const today = startOfDay(new Date());
    const maxDate = addDays(today, restaurant?.settings?.max_advance_days || 30);
    return !isBefore(date, today) && !isBefore(maxDate, date);
  };

  const nextStep = () => setStep(s => Math.min(s + 1, 3));
  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  // Dynamic styles based on restaurant branding
  const primaryColor = restaurant?.branding?.primary_color || '#1a1f2c';
  const heroImage = restaurant?.branding?.hero_image_url;
  const logoUrl = restaurant?.branding?.logo_url;
  const tagline = restaurant?.branding?.tagline;
  const currencySymbol = CURRENCY_SYMBOLS[depositInfo?.currency || 'gbp'] || '£';
  const deductTransactionFees = Boolean(depositInfo?.deduct_transaction_fees_from_refunds);
  const eligibleRefundText = deductTransactionFees
    ? 'your deposit minus the original card processing fee charged by Stripe'
    : 'a full refund of your deposit';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="font-heading text-xl font-semibold mb-2">Restaurant Not Found</h2>
            <p className="text-muted-foreground">The booking widget you're looking for doesn't exist.</p>
            <Link to="/">
              <Button className="mt-4">Go Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment success/cancel pages
  const paymentCancelled = window.location.pathname.includes('/payment-cancelled');
  if (searchParams.get('session_id') || paymentCancelled) {
    const bookingId = searchParams.get('booking_id');
    
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="payment-result">
        <div className="max-w-lg mx-auto">
          {/* Hero Image & Header */}
          {heroImage ? (
            <div className="relative rounded-2xl overflow-hidden mb-6 shadow-lg">
              <img 
                src={heroImage} 
                alt={restaurant.name}
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt="Logo"
                      className="h-12 w-12 rounded-lg object-contain bg-white/10 p-1"
                    />
                  ) : (
                    <div 
                      className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-bold text-lg bg-white/20"
                    >
                      {restaurant.name[0]}
                    </div>
                  )}
                  <div>
                    <h1 className="font-heading text-2xl font-bold drop-shadow-lg">{restaurant.name}</h1>
                    <p className="text-white/80 text-sm">{tagline || 'Book a Table'}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center mb-6">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={restaurant.name}
                  className="h-16 w-auto mx-auto mb-3 rounded-lg"
                />
              ) : (
                <div 
                  className="inline-flex h-14 w-14 rounded-xl items-center justify-center text-white font-bold text-xl mb-3"
                  style={{ backgroundColor: primaryColor }}
                >
                  {restaurant.name[0]}
                </div>
              )}
              <h1 className="font-heading text-2xl font-bold">{restaurant.name}</h1>
              <p className="text-muted-foreground text-sm mt-1">{tagline || 'Book a Table'}</p>
            </div>
          )}

          <Card className="shadow-lg border-border/50">
            <CardContent className="p-8">
              {paymentProcessing ? (
                <div className="text-center">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" style={{ color: primaryColor }} />
                  <h2 className="font-heading text-xl font-semibold mb-2">Checking Status...</h2>
                  <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
                </div>
              ) : paymentStatus === 'success' && confirmation ? (
                <>
                  {/* Success Icon */}
                  <div className="text-center mb-6">
                    <div 
                      className="h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <CheckCircle className="h-10 w-10" style={{ color: primaryColor }} />
                    </div>
                    <h2 className="font-heading text-2xl font-bold mb-2">Booking Confirmed!</h2>
                    <p className="text-muted-foreground">
                      Your deposit has been received and your table is reserved.
                    </p>
                  </div>

                  {/* Booking Details Card */}
                  <div className="bg-slate-50 rounded-xl p-5 mb-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Date</p>
                        <p className="font-semibold">
                          {confirmation.booking_date ? format(new Date(confirmation.booking_date), 'EEEE, MMMM d, yyyy') : selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Time</p>
                        <p className="font-semibold">{confirmation.booking_time || selectedTime}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Guests</p>
                        <p className="font-semibold">{confirmation.party_size || partySize} people</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Confirmation</p>
                        <p className="font-semibold font-mono">#{(confirmation.id || bookingId || '').slice(0, 8).toUpperCase()}</p>
                      </div>
                    </div>
                    
                    {/* Deposit Paid Badge */}
                    {confirmation.deposit_amount > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Deposit Paid</span>
                          <span className="font-bold text-green-600">{currencySymbol}{confirmation.deposit_amount?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Restaurant Info */}
                  <div className="space-y-2 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{restaurant.address}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span>{restaurant.phone}</span>
                    </div>
                  </div>

                  {/* Add to Calendar */}
                  <div className="border-t pt-5">
                    <p className="text-sm font-medium text-center mb-3">Add to Calendar</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {(() => {
                        const calLinks = generateCalendarLinks(confirmation, restaurant, selectedDate);
                        if (!calLinks) {
                          return (
                            <p className="text-center text-xs text-muted-foreground">
                              Calendar links will be available in your confirmation email.
                            </p>
                          );
                        }
                        return (
                          <>
                            <a 
                              href={calLinks.google} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium transition-colors"
                            >
                              📅 Google
                            </a>
                            <a 
                              href={calLinks.outlook}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium transition-colors"
                            >
                              📅 Outlook
                            </a>
                            <a 
                              href={calLinks.ics}
                              download={calLinks.icsFilename}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium transition-colors"
                            >
                              📅 Download
                            </a>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Email Confirmation Note */}
                  <p className="text-xs text-center text-muted-foreground mt-6">
                    ✉️ A confirmation email has been sent to your email address
                  </p>
                </>
              ) : paymentStatus === 'pending_confirmation' ? (
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-amber-50">
                    <AlertCircle className="h-8 w-8 text-amber-500" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold mb-2">Payment Still Confirming</h2>
                  <p className="text-muted-foreground mb-4">
                    Stripe has not confirmed the final payment status yet. Your booking will only be confirmed once Stripe reports the deposit as paid.
                  </p>
                  <Button
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => checkPaymentStatus(searchParams.get('session_id'), bookingId)}
                  >
                    Check Again
                  </Button>
                </div>
              ) : paymentStatus === 'failed' || paymentStatus === 'error' || paymentCancelled ? (
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-50">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold mb-2">Payment Failed</h2>
                  <p className="text-muted-foreground mb-4">
                    We couldn't confirm your payment, so your booking has not been confirmed yet. Please try the deposit payment again.
                  </p>
                  <Link to={bookingId ? `/book/${restaurantId}/pay/${bookingId}` : `/book/${restaurantId}`}>
                    <Button style={{ backgroundColor: primaryColor }}>Try Again</Button>
                  </Link>
                </div>
              ) : (
                <div className="text-center">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
                  <h2 className="font-heading text-xl font-semibold mb-2">Checking Status...</h2>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span>
              Powered by <span className="font-semibold">DineSpot</span>
            </span>
            <span className="text-muted-foreground/50" aria-hidden>
              ·
            </span>
            <Link to="/privacy" className="underline hover:text-foreground">
              Privacy
            </Link>
            <Link to="/terms" className="underline hover:text-foreground">
              Terms
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="booking-widget">
      <div className="max-w-lg mx-auto">
        {/* Hero Image & Header */}
        {heroImage ? (
          <div className="relative rounded-2xl overflow-hidden mb-6 shadow-lg animate-fade-in">
            <img 
              src={heroImage} 
              alt={restaurant.name}
              className="w-full h-48 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Logo"
                    className="h-12 w-12 rounded-lg object-contain bg-white/10 p-1"
                  />
                ) : (
                  <div 
                    className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-bold text-lg bg-white/20"
                  >
                    {restaurant.name[0]}
                  </div>
                )}
                <div>
                  <h1 className="font-heading text-2xl font-bold drop-shadow-lg">{restaurant.name}</h1>
                  <p className="text-white/80 text-sm">{tagline || 'Book a Table'}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center mb-6 animate-fade-in">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Logo"
                className="h-16 w-16 mx-auto mb-3 rounded-xl object-contain"
              />
            ) : (
              <div 
                className="inline-flex h-14 w-14 rounded-xl items-center justify-center text-white font-bold text-xl mb-3"
                style={{ backgroundColor: primaryColor }}
              >
                {restaurant.name[0]}
              </div>
            )}
            <h1 className="font-heading text-2xl font-bold">{restaurant.name}</h1>
            <p className="text-muted-foreground text-sm mt-1">{tagline || 'Book a Table'}</p>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.slice(0, -1).map((s, i) => (
            <div key={s.id} className="flex items-center">
              <div 
                className={cn(
                  "h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
                  i <= step 
                    ? "text-white" 
                    : "bg-secondary text-muted-foreground"
                )}
                style={{ backgroundColor: i <= step ? primaryColor : undefined }}
              >
                {i + 1}
              </div>
              {i < STEPS.length - 2 && (
                <div className={cn(
                  "w-8 h-0.5 mx-1",
                  i < step ? "bg-primary" : "bg-secondary"
                )} style={{ backgroundColor: i < step ? primaryColor : undefined }} />
              )}
            </div>
          ))}
        </div>

        {/* Main Card */}
        <Card className="shadow-lg border-border/50 overflow-hidden animate-scale-in">
          {/* Step 0: Date Selection */}
          {step === 0 && (
            <>
              <CardHeader className="border-b border-border/50 bg-slate-50/50">
                <CardTitle className="font-heading flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  Select a Date
                </CardTitle>
                <CardDescription>Choose when you'd like to visit</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => !canSelectDate(date)}
                    className="rounded-md border"
                    data-testid="date-calendar"
                  />
                </div>
                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={nextStep} 
                    disabled={!selectedDate}
                    style={{ backgroundColor: primaryColor }}
                    data-testid="next-step-btn"
                  >
                    Continue
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 1: Time & Party Size */}
          {step === 1 && (
            <>
              <CardHeader className="border-b border-border/50 bg-slate-50/50">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Choose Time & Guests
                </CardTitle>
                <CardDescription>
                  {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Top Custom Message */}
                {restaurant.settings?.booking_message_top && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800">{restaurant.settings.booking_message_top}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Number of Guests</Label>
                  <Select value={partySize} onValueChange={setPartySize}>
                    <SelectTrigger data-testid="party-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: restaurant.settings.max_party_size }, (_, i) => i + 1).map(n => (
                        <SelectItem key={n} value={String(n)}>
                          {n} {n === 1 ? 'guest' : 'guests'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Deposit Notice with Terms */}
                {depositInfo?.deposit_required && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CreditCard className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800">Deposit Required</p>
                        <p className="text-sm text-amber-700 mt-1">
                          A deposit of <strong>{currencySymbol}{depositInfo.amount.toFixed(2)}</strong> is required for this booking
                          {depositInfo.deposit_type === 'per_person' && ` (${currencySymbol}${(depositInfo.amount / parseInt(partySize)).toFixed(2)} per person)`}.
                        </p>
                        <div className="mt-2 p-2 bg-amber-100/50 rounded text-xs text-amber-800">
                          <strong>Cancellation Policy:</strong> {depositInfo.is_refundable ? (
                            <>Cancellations made more than <strong>{depositInfo.refund_hours_before} hours</strong> before your reservation will receive {eligibleRefundText}. Cancellations within {depositInfo.refund_hours_before} hours or no-shows will forfeit the deposit. If your booking is moved at your request within {depositInfo.refund_hours_before} hours of the original time, the deposit is carried over and becomes non-refundable.</>
                          ) : (
                            <>This deposit is non-refundable.</>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Available Times</Label>
                  {loadingSlots ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : slots.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No available slots for this date</p>
                      <Button variant="outline" size="sm" className="mt-3" onClick={prevStep}>
                        Choose another date
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Group slots by period if they have period_title */}
                      {(() => {
                        // Filter out slots with no availability
                        const availableSlots = slots.filter(slot => slot.available_tables > 0);
                        
                        const periodGroups = {};
                        availableSlots.forEach(slot => {
                          const period = slot.period_title || 'Available Times';
                          if (!periodGroups[period]) periodGroups[period] = [];
                          periodGroups[period].push(slot);
                        });
                        const periods = Object.keys(periodGroups);
                        
                        return periods.length === 1 && periods[0] === 'Available Times' ? (
                          <div className="grid grid-cols-3 gap-2">
                            {availableSlots.map((slot) => (
                              <button
                                key={slot.time}
                                onClick={() => setSelectedTime(slot.time)}
                                className={cn(
                                  "p-3 rounded-lg border text-center transition-all",
                                  selectedTime === slot.time
                                    ? "border-2 text-white"
                                    : "border-border hover:border-primary/50 hover:bg-secondary/50"
                                )}
                                style={{ 
                                  backgroundColor: selectedTime === slot.time ? primaryColor : undefined,
                                  borderColor: selectedTime === slot.time ? primaryColor : undefined
                                }}
                                data-testid={`time-slot-${slot.time}`}
                              >
                                <span className="font-medium">{slot.time}</span>
                                <span className={cn(
                                  "block text-xs mt-0.5",
                                  selectedTime === slot.time ? "text-white/80" : "text-muted-foreground"
                                )}>
                                  {slot.available_tables} {slot.available_tables === 1 ? 'table' : 'tables'}
                                </span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {periods.map(period => (
                              <div key={period}>
                                <h4 className="text-sm font-medium text-muted-foreground mb-2">{period}</h4>
                                <div className="grid grid-cols-3 gap-2">
                                  {periodGroups[period].filter(slot => slot.available_tables > 0).map((slot) => (
                                    <button
                                      key={slot.time}
                                      onClick={() => setSelectedTime(slot.time)}
                                      className={cn(
                                        "p-3 rounded-lg border text-center transition-all",
                                        selectedTime === slot.time
                                          ? "border-2 text-white"
                                          : "border-border hover:border-primary/50 hover:bg-secondary/50"
                                      )}
                                      style={{ 
                                        backgroundColor: selectedTime === slot.time ? primaryColor : undefined,
                                        borderColor: selectedTime === slot.time ? primaryColor : undefined
                                      }}
                                      data-testid={`time-slot-${slot.time}`}
                                    >
                                      <span className="font-medium">{slot.time}</span>
                                      <span className={cn(
                                        "block text-xs mt-0.5",
                                        selectedTime === slot.time ? "text-white/80" : "text-muted-foreground"
                                      )}>
                                        {slot.available_tables} {slot.available_tables === 1 ? 'table' : 'tables'}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>

                {/* Bottom Custom Message */}
                {restaurant.settings?.booking_message_bottom && (
                  <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
                    <p className="text-sm text-slate-700">{restaurant.settings.booking_message_bottom}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={prevStep} className="flex-1">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                  <Button 
                    onClick={nextStep} 
                    disabled={!selectedTime}
                    className="flex-1"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="next-step-btn"
                  >
                    Continue
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Contact Details */}
          {step === 2 && (
            <>
              <CardHeader className="border-b border-border/50 bg-slate-50/50">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Your Details
                </CardTitle>
                <CardDescription>
                  {selectedDate && format(selectedDate, 'MMM d')} at {selectedTime} • {partySize} {parseInt(partySize) === 1 ? 'guest' : 'guests'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      data-testid="booking-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@email.com"
                      required
                      data-testid="booking-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+44 7700 900000"
                      required
                      data-testid="booking-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="allergies" className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      Allergies / Dietary Requirements
                    </Label>
                    <Textarea
                      id="allergies"
                      value={formData.allergies}
                      onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                      placeholder="e.g., Nut allergy, Gluten-free, Vegetarian..."
                      rows={2}
                      data-testid="booking-allergies"
                      className="border-amber-200 focus:border-amber-400"
                    />
                    <p className="text-xs text-muted-foreground">
                      Please list any allergies or dietary needs so our staff can accommodate you.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="requests">Special Requests (optional)</Label>
                    <Textarea
                      id="requests"
                      value={formData.specialRequests}
                      onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                      placeholder="Special occasion, seating preferences..."
                      rows={2}
                      data-testid="booking-requests"
                    />
                  </div>

                  {/* Deposit Summary with Terms */}
                  {depositInfo?.deposit_required && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-border/50 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Deposit Required</span>
                        <span className="font-bold text-lg">{currencySymbol}{depositInfo.amount.toFixed(2)}</span>
                      </div>
                      
                      {/* Terms & Conditions */}
                      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                        <button
                          type="button"
                          onClick={() => setShowFullTerms(!showFullTerms)}
                          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                        >
                          <span className="flex items-center gap-2 text-sm font-medium">
                            <FileText className="h-4 w-4 text-slate-500" />
                            Terms & Conditions
                          </span>
                          {showFullTerms ? (
                            <ChevronUp className="h-4 w-4 text-slate-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-slate-500" />
                          )}
                        </button>
                        
                        {showFullTerms && (
                          <div className="border-t border-slate-200">
                            <ScrollArea className="h-[200px] p-4">
                              <div className="text-xs text-slate-600 whitespace-pre-wrap font-mono leading-relaxed">
                                {restaurant?.deposit_settings?.terms_and_conditions
                                  ?.replaceAll('{refund_hours}', String(depositInfo.refund_hours_before || 24))
                                  ?.replaceAll(
                                    '{refund_fee_policy}',
                                    deductTransactionFees
                                      ? 'Eligible refunds are returned minus the original card processing fee charged by Stripe.'
                                      : 'Eligible refunds are returned in full.'
                                  ) || `BOOKING DEPOSIT TERMS & CONDITIONS

By paying this deposit, you agree to the following terms:

1. DEPOSIT PURPOSE
Restaurants prepare staffing, tables and ingredients around confirmed bookings. This deposit secures your reservation, helps reduce last-minute no-shows, and will be deducted from your final bill.

2. CANCELLATION POLICY
• Cancellations made more than ${depositInfo.refund_hours_before || 24} hours before your booking: ${deductTransactionFees ? 'Deposit refunded minus the original card processing fee charged by Stripe' : 'Full refund of deposit'}
• Cancellations made less than ${depositInfo.refund_hours_before || 24} hours before your booking: Deposit is non-refundable
• No-shows: Deposit is non-refundable

3. CHANGES AND MOVED BOOKINGS
If you need to change your booking (date, time or party size), please contact us as soon as possible. Changes are subject to availability and must be agreed with us.
If we move your booking to a new date or time at your request, your deposit is carried over to the new booking. Whether it remains refundable depends on when the move is made:
• Moves made more than ${depositInfo.refund_hours_before || 24} hours before your ORIGINAL booking time: your deposit stays refundable. The cancellation policy above then applies to your NEW booking time.
• Moves made less than ${depositInfo.refund_hours_before || 24} hours before your ORIGINAL booking time (when your deposit would already be non-refundable): your deposit is carried over as NON-REFUNDABLE. It will not become refundable again, even if your new booking is more than ${depositInfo.refund_hours_before || 24} hours away, and it will not be refunded if you later cancel, move again or do not attend.
A deposit carried over to a moved booking is still deducted from your final bill. We will confirm any move, and what it means for your deposit, by email.

4. REFUNDS
Refunds will be processed within 5-10 business days to your original payment method. ${deductTransactionFees ? 'If card processing fees are deducted, only the net refundable deposit amount is returned.' : ''}

5. AGREEMENT
These terms constitute an agreement between you and ${restaurant?.name || 'the restaurant'}. By proceeding with payment, you confirm that you have read, understood, and agree to these terms.

For any questions, please contact the restaurant directly.`}
                              </div>
                            </ScrollArea>
                          </div>
                        )}
                      </div>

                      {/* Quick Summary */}
                      <div className="p-3 bg-amber-50 rounded border border-amber-200 text-xs text-amber-800">
                        <p className="font-medium mb-1">Quick Summary:</p>
                        {depositInfo.is_refundable ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            <li>Cancel more than <strong>{depositInfo.refund_hours_before} hours</strong> before = {deductTransactionFees ? 'refund minus Stripe card fees' : 'full refund'}</li>
                            <li>Cancel within {depositInfo.refund_hours_before} hours or no-show = Deposit forfeited</li>
                            <li>Booking moved at your request within {depositInfo.refund_hours_before} hours of the original time = deposit carried over and <strong>non-refundable</strong> (it does not become refundable again)</li>
                          </ul>
                        ) : (
                          <p>This deposit is <strong>non-refundable</strong> regardless of cancellation.</p>
                        )}
                      </div>

                      {/* Terms Acceptance Checkbox */}
                      <div className="flex items-start gap-3 p-3 bg-white rounded border border-slate-200">
                        <Checkbox
                          id="terms-acceptance"
                          checked={termsAccepted}
                          onCheckedChange={setTermsAccepted}
                          className="mt-0.5"
                          data-testid="terms-checkbox"
                        />
                        <label 
                          htmlFor="terms-acceptance" 
                          className="text-sm text-slate-700 cursor-pointer leading-relaxed"
                        >
                          I have read and agree to the <button type="button" onClick={() => setShowFullTerms(true)} className="text-primary underline font-medium">Terms & Conditions</button>. I understand this is an agreement between myself and {restaurant?.name || 'the restaurant'}.
                        </label>
                      </div>

                      <p className="text-xs text-muted-foreground text-center">
                        You'll be redirected to Stripe to complete your {currencySymbol}{depositInfo.amount.toFixed(2)} deposit payment.
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={prevStep} className="flex-1">
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Back
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submitting || !formData.name || !formData.email || !formData.phone || (depositInfo?.deposit_required && !termsAccepted)}
                      className="flex-1"
                      style={{ backgroundColor: primaryColor }}
                      data-testid="confirm-booking-btn"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : depositInfo?.deposit_required ? (
                        <>
                          {termsAccepted ? 'Pay Deposit' : 'Accept Terms to Continue'}
                          <CreditCard className="h-4 w-4 ml-1" />
                        </>
                      ) : (
                        <>
                          Confirm Booking
                          <CheckCircle className="h-4 w-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && confirmation && !searchParams.get('session_id') && (
            <>
              <CardContent className="p-8 text-center">
                {/* Logo or Restaurant Initial */}
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Logo"
                    className="h-16 w-16 mx-auto mb-4 rounded-xl object-contain"
                  />
                ) : (
                  <div 
                    className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {restaurant.name[0]}
                  </div>
                )}
                
                <div 
                  className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4 -mt-10"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                
                <h2 className="font-heading text-2xl font-bold mb-2">Booking Confirmed!</h2>
                <p className="text-muted-foreground mb-6">
                  We've sent a confirmation to {formData.email}
                </p>

                <div className="bg-slate-50 rounded-xl p-6 text-left space-y-3">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
                      <p className="text-sm text-muted-foreground">at {confirmation.time}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <p>{confirmation.party_size} {confirmation.party_size === 1 ? 'guest' : 'guests'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <p>{restaurant.address}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <p>{restaurant.phone}</p>
                  </div>
                </div>

                {/* Add to Calendar Section */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="text-sm font-medium text-muted-foreground mb-3">Add to Calendar</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {(() => {
                      const calLinks = generateCalendarLinks(confirmation, restaurant, selectedDate);
                      return (
                        <>
                          <a 
                            href={calLinks.google} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Google
                          </a>
                          <a 
                            href={calLinks.outlook}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium transition-colors"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0078D4">
                              <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.362.228-.61.228h-8.234v-6.182l1.674 1.18c.08.063.17.095.266.095.104 0 .192-.032.266-.095l.023-.023 5.58-3.932V7.387zm-.848-.617l-6.39 4.506-1.842-1.298V3.33h7.38c.248 0 .452.076.61.228.158.152.238.347.238.576v2.636h.004zm-9.08 11.9H0V5.752c0-.63.324-.994.97-.994H14.072V18.67zm-8.08-9.376v6.618h5.338v-6.618L8.012 12.19l-1.678-2.895H6.04l-.048-.001zm2.068 1.2l1.784 3.076 1.87-3.077h-3.654v.001z"/>
                            </svg>
                            Outlook
                          </a>
                          <a 
                            href={calLinks.ics}
                            download={calLinks.icsFilename}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium transition-colors"
                          >
                            <CalendarIcon className="w-4 h-4" />
                            Apple / Download
                          </a>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-6">
                  Confirmation #{confirmation.id.slice(0, 8).toUpperCase()}
                </p>
              </CardContent>
            </>
          )}
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <span>
            Powered by <span className="font-semibold">DineSpot</span>
          </span>
          <span className="text-muted-foreground/50" aria-hidden>
            ·
          </span>
          <Link to="/privacy" className="underline hover:text-foreground">
            Privacy
          </Link>
          <Link to="/terms" className="underline hover:text-foreground">
            Terms
          </Link>
        </p>
      </div>
    </div>
  );
}
